// PasteObjectsModal — the paste step of the cross-connection object clipboard.
// Shows source → target, the objects being copied, and the copy options (data,
// conflict policy, definer). Running calls /api/object-copy and then shows a
// per-object result report. See OBJECT_COPY_SPEC.md.
import { createSignal, createMemo, createResource, Show, For } from 'solid-js';
import Modal from './Modal.jsx';
import { api } from '../lib/api.js';
import { copyObjects } from '../lib/objectcopy.js';
import { toast } from '../lib/toast.js';

export default function PasteObjectsModal(props) {
  const cb = props.clipboard;            // { sourceConnId, sourceConnName, sourceDb, items }
  const target = props.target;           // { connectionId, connectionName, database }
  const hasTables = createMemo(() => (cb?.items || []).some((o) => o.kind === 'table'));
  const crossName = () => cb && target && cb.sourceDb !== target.database;

  // Same connection AND database → a paste must RENAME each object (you can't have
  // two objects with the same name in one schema). Names default to the old name
  // and Paste is blocked until each is changed (and unique).
  const sameTarget = () => cb && target && cb.sourceConnId === target.connectionId && cb.sourceDb === target.database;
  const keyOf = (o) => `${o.kind}:${o.name}`;
  const [names, setNames] = createSignal(Object.fromEntries((cb?.items || []).map((o) => [keyOf(o), o.name])));
  const setName = (o, v) => setNames((m) => ({ ...m, [keyOf(o)]: v }));
  const nameOf = (o) => names()[keyOf(o)] ?? o.name;

  // Existing object names in the TARGET schema — a renamed copy may not collide with
  // any of them (this also covers an unchanged name, since the original is in there).
  const [existing] = createResource(
    () => (sameTarget() ? [target.connectionId, target.database] : null),
    async ([id, db]) => {
      try {
        const r = await api.get(`/api/connections/${id}/databases/${encodeURIComponent(db)}/objects`);
        const s = new Set();
        for (const k of ['tables', 'views', 'functions', 'events']) for (const o of (r[k] || [])) s.add(String(o.name).toLowerCase());
        return s;
      } catch { return new Set(); }
    },
  );
  // Why a new name is invalid (for red styling + the disabled gate). null = valid.
  const nameError = (o) => {
    const v = nameOf(o).trim();
    if (!v) return 'empty';
    const ex = existing();
    if (ex && ex.has(v.toLowerCase())) return 'exists';                         // collides with a schema object (incl. the unchanged original)
    const dup = (cb?.items || []).filter((x) => nameOf(x).trim().toLowerCase() === v.toLowerCase()).length > 1;
    if (dup) return 'dup';
    return null;
  };
  const renamesValid = () => {
    if (existing.loading) return false;                                         // wait until we know the schema
    return (cb?.items || []).every((o) => !nameError(o));
  };
  const canRun = () => !sameTarget() || renamesValid();

  const [data, setData] = createSignal(false);            // copy table row data
  const [onConflict, setOnConflict] = createSignal('skip'); // skip | overwrite
  const [dataMode, setDataMode] = createSignal('append');   // append | truncate
  const [stripDefiner, setStripDefiner] = createSignal(true);
  const [triggers, setTriggers] = createSignal(true);

  const [running, setRunning] = createSignal(false);
  const [report, setReport] = createSignal(null);          // { results, created, skipped, failed }

  const run = async () => {
    setRunning(true);
    try {
      const objects = (cb.items || []).map((o) =>
        sameTarget() ? { kind: o.kind, name: o.name, targetName: nameOf(o).trim() } : { kind: o.kind, name: o.name });
      const r = await copyObjects({
        source: { connectionId: cb.sourceConnId, database: cb.sourceDb, objects },
        target: { connectionId: target.connectionId, database: target.database },
        options: {
          data: data() && hasTables(),
          onConflict: onConflict(),
          dataMode: dataMode(),
          stripDefiner: stripDefiner(),
          triggers: triggers(),
        },
      });
      setReport(r);
      if (r.failed > 0) toast.error(`Copied with ${r.failed} failure${r.failed === 1 ? '' : 's'} — see report.`);
      else toast.success(`Copied ${r.created} object${r.created === 1 ? '' : 's'}${r.skipped ? `, ${r.skipped} skipped` : ''}.`);
      props.onDone?.();
    } catch (e) {
      toast.error('Copy failed: ' + (e?.message || e));
    } finally {
      setRunning(false);
    }
  };

  const statusIcon = (s) => (s === 'created' ? '✓' : s === 'skipped' ? '–' : '✕');

  return (
    <Modal open={true} onClose={props.onClose}
           title={`Paste ${(cb?.items || []).length} object${(cb?.items || []).length === 1 ? '' : 's'}`}>
      <div class="paste-objects">
        <div class="paste-route mono">
          <span>{cb?.sourceConnName || `conn#${cb?.sourceConnId}`} / {cb?.sourceDb}</span>
          <span class="paste-arrow">→</span>
          <span>{target?.connectionName || `conn#${target?.connectionId}`} / {target?.database}</span>
        </div>

        <Show when={!report()} fallback={
          // ── Result report ──
          <div class="paste-report">
            <div class="paste-report-summary">
              <span class="hist-ok">✓ {report().created} created</span>
              <Show when={report().skipped}><span class="muted">– {report().skipped} skipped</span></Show>
              <Show when={report().failed}><span class="hist-err">✕ {report().failed} failed</span></Show>
            </div>
            <div class="paste-report-list">
              <For each={report().results}>
                {(r) => (
                  <div class="paste-report-row" classList={{ 'paste-report-fail': r.status === 'failed' }}>
                    <span class={`paste-report-status paste-${r.status}`}>{statusIcon(r.status)}</span>
                    <span class="paste-report-kind">{r.kind}</span>
                    <span class="paste-report-name mono">{r.name}</span>
                    <Show when={r.rows}><span class="muted">{r.rows} rows</span></Show>
                    <Show when={r.error}><span class="paste-report-err" title={r.error}>{r.error}</span></Show>
                  </div>
                )}
              </For>
            </div>
            <div style="display:flex;justify-content:flex-end;margin-top:14px;">
              <button class="btn" onClick={props.onClose}>Close</button>
            </div>
          </div>
        }>
          {/* ── Objects (with rename inputs when pasting into the same database) ── */}
          <Show when={sameTarget()}>
            <div class="import-warn import-warn-soft">
              Same database — give each object a <strong>new name</strong> to create a copy alongside the original
              (a name can't match the original or any other object already in this schema).
            </div>
          </Show>
          <div class="import-preview-wrap" style="max-height:200px;margin-bottom:12px;">
            <table class="import-preview">
              <thead><tr><th>Kind</th><th>Object</th><Show when={sameTarget()}><th>New name</th></Show></tr></thead>
              <tbody>
                <For each={cb?.items || []}>
                  {(o) => (
                    <tr>
                      <td>{o.kind}</td>
                      <td class="mono">{o.name}</td>
                      <Show when={sameTarget()}>
                        <td>
                          <input class="input mono" style="width:100%;min-width:160px;"
                                 classList={{ 'paste-name-bad': !!nameError(o) }}
                                 title={{ empty: 'Enter a name', exists: 'An object with this name already exists in this schema', dup: 'Two objects map to the same name' }[nameError(o)] || ''}
                                 value={nameOf(o)} onInput={(e) => setName(o, e.currentTarget.value)}
                                 placeholder={`${o.name}_copy`} />
                        </td>
                      </Show>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>

          <Show when={crossName()}>
            <div class="import-warn import-warn-soft">
              Source database (<span class="mono">{cb.sourceDb}</span>) differs from the target
              (<span class="mono">{target.database}</span>). View/routine bodies that hard-reference
              <span class="mono"> `{cb.sourceDb}`.</span> won't be rewritten — best for same-named databases.
            </div>
          </Show>

          <Show when={hasTables()}>
            <div class="import-section-title">Table data</div>
            <label class="checkbox-label">
              <input type="checkbox" checked={data()} onChange={(e) => setData(e.currentTarget.checked)} />
              <span>Copy row data (not just structure)</span>
            </label>
            <Show when={data() && !sameTarget()}>
              <div class="import-radios" style="margin-top:6px;">
                <label class="import-radio">
                  <input type="radio" name="pdm" checked={dataMode() === 'append'} onChange={() => setDataMode('append')} />
                  <span>Append <span class="muted">(insert into the new table)</span></span>
                </label>
                <label class="import-radio">
                  <input type="radio" name="pdm" checked={dataMode() === 'truncate'} onChange={() => setDataMode('truncate')} />
                  <span>Truncate first <span class="muted">(empty the target table before inserting)</span></span>
                </label>
              </div>
            </Show>
          </Show>

          {/* Conflict policy is moot for a same-database copy — the new names are
              validated to not exist, so nothing can be overwritten. */}
          <Show when={!sameTarget()}>
            <div class="import-section-title">If an object already exists on the target</div>
            <div class="import-radios">
              <label class="import-radio">
                <input type="radio" name="poc" checked={onConflict() === 'skip'} onChange={() => setOnConflict('skip')} />
                <span>Skip <span class="muted">(leave the existing object untouched)</span></span>
              </label>
              <label class="import-radio">
                <input type="radio" name="poc" checked={onConflict() === 'overwrite'} onChange={() => setOnConflict('overwrite')} />
                <span>Overwrite <span class="muted">(DROP the existing object, then recreate)</span></span>
              </label>
            </div>
          </Show>

          <div class="import-nulls" style="margin-top:10px;">
            <label class="checkbox-label">
              <input type="checkbox" checked={stripDefiner()} onChange={(e) => setStripDefiner(e.currentTarget.checked)} />
              <span>Strip <span class="mono">DEFINER</span> <span class="muted">(run as the target's caller)</span></span>
            </label>
            <Show when={hasTables()}>
              <label class="checkbox-label">
                <input type="checkbox" checked={triggers()} onChange={(e) => setTriggers(e.currentTarget.checked)} />
                <span>Include each copied table's triggers <span class="muted">(renamed to match the new table)</span></span>
              </label>
            </Show>
          </div>

          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px;">
            <button class="btn btn-ghost" onClick={props.onClose} disabled={running()}>Cancel</button>
            <button class="btn btn-primary" onClick={run} disabled={running() || !canRun()}
                    title={!canRun() ? 'Give each object a new, unique name (same database)' : ''}>
              {running() ? 'Copying…' : '📥 Paste'}
            </button>
          </div>
        </Show>
      </div>
    </Modal>
  );
}
